import { native } from '@forest/native-services'
import { useStore } from '@nanostores/solid'
import { Show } from 'solid-js'
import arrowLeftSvg from '~/assets/figma/arrow-left-vuesax.svg'
import coinPng from '~/assets/figma/coin.png'
import gemPng from '~/assets/figma/gem.png'
import shopAwningPng from '~/assets/figma/shop-awning.png'
import stampSvg from '~/assets/figma/stamp.svg'
import { eventEndDate } from '~/config'
import { useCountdown } from '~/hooks/use-countdown'
import { useEffectiveNow, useEventEnded } from '~/hooks/use-event-ended'
import { isDebugEnv } from '~/lib/env'
import { m } from '~/paraglide/messages'
import { useActivityTokenBalance } from '~/resources/activity-token-balance'
import { useUser } from '~/resources/user'
import chipIconSvg from '~/assets/boosting-banner/chip-icon.svg'
import chipReflectionSvg from '~/assets/boosting-banner/chip-reflection.svg'
import {
  $isBoostingBannerDismissed,
  $isBoostingBannerExiting,
} from '~/store/boosting-banner'
import { openBoostingPacks } from '~/store/boosting-packs'
import { DebugTool } from './debug-tool'
import { CloseIcon } from './icon/close'
import { HourglassIcon } from './icon/hourglass'

const formatNumber = (n: number) => n.toLocaleString('en-US')

export type TopNavVariant = 'home' | 'shop'

export function TopNav(props: {
  variant?: TopNavVariant
  onOpenShop?: () => void
  onBack?: () => void
}) {
  const variant = () => props.variant ?? 'home'
  // The Boosting Packs banner is the primary entry while it's up; the top-row
  // button only takes over once the banner is dismissed (TopNav only mounts
  // after the event starts, so "not dismissed" == "banner showing" here).
  // During the banner's exit animation we also mount the chip so its pop-in
  // can overlap the banner's flight for a continuous hand-off.
  const boostingBannerDismissed = useStore($isBoostingBannerDismissed)
  const boostingBannerExiting = useStore($isBoostingBannerExiting)
  const now = useEffectiveNow()
  const ended = useEventEnded()
  const countdown = useCountdown(() => ({
    now: now(),
    endDate: eventEndDate,
  }))
  const balanceQuery = useActivityTokenBalance()
  const userQuery = useUser()
  const stamps = () => balanceQuery.data?.balance ?? 0
  const coins = () => userQuery.data?.coin ?? 0
  const gems = () => userQuery.data?.gem ?? 0

  return (
    <header class="pointer-events-none fixed inset-x-0 top-0 z-50 flex flex-col gap-2 pb-3">
      <Show when={variant() === 'shop'} fallback={<HomeBackdrop />}>
        <ShopBackdrop />
      </Show>

      <div
        aria-hidden
        class="relative h-[var(--safe-area-top,0px)] min-h-9 w-full"
      />

      <div class="pointer-events-auto relative flex w-full flex-col px-2">
        <div class="flex w-full items-center justify-between py-2">
          <Show
            when={variant() === 'shop'}
            fallback={
              <button
                type="button"
                onClick={() => native.general.close()}
                class="grid size-[42px] place-items-center overflow-clip rounded-[28px] text-white transition-transform duration-150 active:scale-90"
              >
                <CloseIcon />
              </button>
            }
          >
            <button
              type="button"
              aria-label={m.back()}
              onClick={props.onBack}
              class="grid size-[42px] place-items-center overflow-clip rounded-[28px] border-[1.75px] border-white/30 bg-white/[0.28] backdrop-blur-[20px] transition-transform [transition-duration:150ms] active:scale-90"
            >
              <img
                src={arrowLeftSvg}
                alt=""
                aria-hidden="true"
                class="size-[21px]"
              />
            </button>
          </Show>

          <div class="flex items-center gap-1">
            <Show when={boostingBannerDismissed() || boostingBannerExiting()}>
              <BoosterButton
                onClick={() => openBoostingPacks()}
                animateIn={boostingBannerExiting()}
              />
            </Show>
            <CurrencyPill
              icon={stampSvg}
              value={stamps()}
              cap={9999}
              onClick={variant() === 'home' ? props.onOpenShop : undefined}
              label={m.gem_store()}
            />
            <CurrencyPill icon={coinPng} value={coins()} cap={9999} />
            <CurrencyPill icon={gemPng} value={gems()} cap={9999} />
          </div>
        </div>

        <Show when={variant() === 'home'}>
          <div
            class="flex items-center gap-[3px] pb-2 pl-1"
            classList={{
              'text-white': !ended(),
              'text-[#E84741]': ended(),
            }}
          >
            <HourglassIcon class="size-5" />
            <span class="whitespace-nowrap text-sm font-bold tabular-nums leading-5">
              {ended()
                ? m.ended_text()
                : countdown().days >= 1
                  ? `${m.countdown_day({ day: countdown().days })} ${m.countdown_hour({ hour: countdown().hours })}`
                  : `${m.countdown_hour({ hour: countdown().hours })} ${m.countdown_minute({ minute: countdown().minutes })}`}
            </span>
          </div>
          <Show when={isDebugEnv}>
            <DebugTool />
          </Show>
        </Show>
      </div>
    </header>
  )
}

function HomeBackdrop() {
  return (
    <div
      aria-hidden
      class="pointer-events-none absolute inset-x-0 top-0 h-[120px] bg-black/[0.01] backdrop-blur-[8px]"
      style={{
        '-webkit-mask-image':
          'linear-gradient(to bottom, black 0%, black 50%, transparent 100%)',
        'mask-image':
          'linear-gradient(to bottom, black 0%, black 50%, transparent 100%)',
      }}
    />
  )
}

function ShopBackdrop() {
  return (
    <>
      {/* 1. Backdrop blur with progressive mask fade so the blur softens
            out toward the bottom edge instead of cutting off abruptly. */}
      <div
        aria-hidden
        class="pointer-events-none absolute inset-x-0 top-0 h-[140px] backdrop-blur-[3.3px]"
        style={{
          '-webkit-mask-image':
            'linear-gradient(to bottom, black 0%, black 55%, transparent 100%)',
          'mask-image':
            'linear-gradient(to bottom, black 0%, black 55%, transparent 100%)',
        }}
      />
      {/* 2. Tan colour wash that fades into the cream page background. */}
      <div
        aria-hidden
        class="pointer-events-none absolute inset-x-0 top-0 h-[140px] [background:linear-gradient(to_bottom,#d1af8a_15%,rgba(235,224,204,0)_82%)]"
      />
      {/* 3. Awning canopy tile (728×97) repeats horizontally so wider
            viewports get more panels instead of a centrally cut-off canopy. */}
      <div
        aria-hidden
        class="pointer-events-none absolute inset-x-0 top-0 h-[97px]"
        style={{
          'background-image': `url(${shopAwningPng})`,
          'background-repeat': 'repeat-x',
          'background-position': 'center top',
          'background-size': '728px 97px',
        }}
      />
    </>
  )
}

function BoosterButton(props: { onClick: () => void; animateIn?: boolean }) {
  return (
    <button
      type="button"
      onClick={props.onClick}
      class="relative flex h-[26px] items-center justify-center gap-[2px] overflow-clip rounded-[25.161px] pl-2 pr-1 transition-transform [transition-duration:150ms] active:scale-95"
      classList={{ 'animate-booster-chip-pop': props.animateIn }}
      style={{
        border: '1.677px solid transparent',
        background:
          'linear-gradient(240.18deg, #EDA4FF 15.854%, #7B72F2 77.139%) padding-box, linear-gradient(45deg, #FFF1F7 0%, #F4BED2 28%, #B898C8 50%, #F4BED2 72%, #FFF1F7 100%) border-box',
        'box-shadow':
          '0 0 8px rgba(244, 190, 210, 0.55), 0 0 16px rgba(187, 81, 253, 0.18)',
      }}
    >
      <span
        class="relative whitespace-nowrap text-[11.742px] font-semibold leading-[25.25px] text-white"
        style={{ 'text-shadow': '0 0 7.496px #bb51fd' }}
      >
        {m.booster_tag()}
      </span>
      <img
        src={chipIconSvg}
        alt=""
        aria-hidden="true"
        class="relative h-4 w-[14px] shrink-0"
      />
      {/* Reflection — Figma 4363:50785. The natural SVG is 283×190 with
          two diagonal strips slashing across most of its width. Anchored to
          the chip's right edge, scaled to ~70px wide so both strips land
          inside the chip's visible rect (overflow-clip trims the rest). */}
      <img
        src={chipReflectionSvg}
        alt=""
        aria-hidden="true"
        class="pointer-events-none absolute right-[-8px] top-1/2 block h-auto w-[80px] max-w-none -translate-y-1/2"
      />
    </button>
  )
}

export function CurrencyPill(props: {
  icon: string
  value: number
  /** When set, values above this show as `${cap}+` (e.g. 9999 -> "9999+"). */
  cap?: number
  onClick?: () => void
  label?: string
}) {
  const interactive = () => props.onClick !== undefined
  const display = () =>
    props.cap !== undefined && props.value > props.cap
      ? `${props.cap}+`
      : formatNumber(props.value)
  return (
    <button
      type="button"
      onClick={props.onClick}
      aria-label={props.label}
      disabled={!interactive()}
      class="flex h-[26px] items-center justify-center gap-1 overflow-clip rounded-[15px] bg-black/20 px-[5px] py-[3px] transition-transform duration-150"
      classList={{ 'active:scale-95': interactive() }}
    >
      <img src={props.icon} alt="" aria-hidden="true" class="size-[18px]" />
      <span class="text-sm tabular-nums leading-5 text-white">{display()}</span>
    </button>
  )
}
